import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute } from '@angular/router';
import {
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  Subject,
  takeUntil
} from 'rxjs';
import { AventuraService } from '../../services/api/aventura.service';
import { FileService } from '../../services/api/file.service';

@Component({
  selector: 'app-configuracion',
  templateUrl: './configuracion.component.html',
  styleUrls: ['./configuracion.component.css']
})
export class ConfiguracionComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private loadRequestId = 0;

  constructor(
    private fileService: FileService,
    private aventuraService: AventuraService,
    private route: ActivatedRoute
  ) {}

  displayedColumns: string[] = ['file', 'servicio'];

  displayedColumns2: string[] = [
    'file',
    'servicio',
    'categoriaProducto',
    'subCategoriaProducto'
  ];
  paxHeader: string[] = [];
  cont = 0;
  paxes: any[] = [];
  filterValue: any;

  dataSource: any;
  location: string | null = null;
  selectFile: string | null = null;
  arrayFiles: any = [];

  selected: any = null;
  dataInitial: any;
  dataFilter: any;
  loader: boolean = false;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        map((params) => params.get('location')),
        filter((location): location is string => !!location),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((location) => {
        this.location = location;
        void this.getAllFiles();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getLocation(location: string) {
    if (location === 'cusco') {
      return 'Cusco';
    } else if (location === 'huaraz') {
      return 'Huaraz';
    }
    return '';
  }

  async getAllFiles(): Promise<void> {
    const currentLoadId = ++this.loadRequestId;
    this.loader = false;
    this.paxes = [];
    this.paxHeader = [];
    this.displayedColumns2 = [
      'file',
      'servicio',
      'categoriaProducto',
      'subCategoriaProducto'
    ];
    this.dataInitial = [];
    this.dataFilter = [];
    this.arrayFiles = [];
    this.selectFile = null;
    this.selected = null;
    this.filterValue = '';

    try {
      const response: any = await firstValueFrom(this.fileService.getAllFiles());
      const files = response?.getAllFiles ?? [];
      const selectedLocation = this.location?.toLowerCase();

      const rowsByFile = await Promise.all(
        files.map(async (file: any) => {
          const detailsResponse: any = await firstValueFrom(
            this.fileService.getDetailsByFile(file.id)
          );
          const services = detailsResponse?.getServicesByFile ?? [];

          const fileRows = await Promise.all(
            services
              .filter((service: any) => {
                const serviceLocation =
                  service.objServicio?.locacion?.toLowerCase();

                return (
                  serviceLocation === selectedLocation &&
                  service.productId &&
                  (service.tipoServicio === 'PERSONALDECAMPO' ||
                    service.tipoServicio === 'GUIA')
                );
              })
              .map(async (service: any) => {
                const [productResponse, pxrResponse] = await Promise.all([
                  firstValueFrom(
                    this.aventuraService.getProductById(service.productId)
                  ),
                  firstValueFrom(
                    this.aventuraService.getServicesPXRByProductId(
                      service.productId
                    )
                  )
                ]);

                const product = productResponse?.getProductById;
                const matchedService = (
                  pxrResponse?.getServicesByProductId ?? []
                ).find((item: any) => item.idServicio === service.serviceId);

                if (!matchedService?.nroPersonasPax?.length) {
                  return null;
                }

                if (service.contador && String(service.contador) !== '1') {
                  return null;
                }

                return {
                  file: file.fileNumber,
                  categoria: 'categoria',
                  servicio: matchedService.nombreServicio,
                  pax: matchedService.nroPersonasPax.map((pax: any) => ({
                    ...pax
                  })),
                  contador: service.contador,
                  location: service.objServicio.locacion,
                  categoriaProducto: product?.categoriaProducto,
                  subCategoriaProducto: product?.subCategoriaProducto,
                  createdAt: file.createdAt,
                  updatedAt: file.updatedAt
                };
              })
          );

          return fileRows.filter((row: any): row is any => row !== null);
        })
      );

      if (currentLoadId !== this.loadRequestId) {
        return;
      }

      this.paxes = rowsByFile.reduce(
        (acc: any[], rows: any[]) => acc.concat(rows),
        []
      );

      const maxPaxLength = this.paxes.reduce(
        (max, item) => Math.max(max, item.pax?.length ?? 0),
        0
      );

      this.paxes = this.paxes.map((item: any) => ({
        ...item,
        pax: [
          ...item.pax,
          ...Array.from({ length: maxPaxLength - item.pax.length }, () => ({
            numPax: '-',
            id: 'PAX'
          }))
        ]
      }));

      this.paxHeader = Array.from(
        { length: maxPaxLength },
        (_, index) => `PAX-${index + 1}`
      );

      this.displayedColumns2 = [
        'file',
        'servicio',
        'categoriaProducto',
        'subCategoriaProducto',
        ...this.paxHeader
      ];

      this.dataInitial = [...this.paxes];
      this.arrayFiles = [
        ...new Set(this.dataInitial.map((item: any) => item.file))
      ];
      this.dataFilter = [...this.paxes];
    } catch (error) {
      console.log(error);
    } finally {
      if (currentLoadId === this.loadRequestId) {
        this.loader = true;
      }
      console.log('FINIH');
    }
  }

  applyFilter(event: Event) {
    this.filterValue = (event.target as HTMLInputElement).value
      .trim()
      .toLowerCase();

    let result = this.dataInitial.filter(
      (element: any) =>
        element.servicio?.toLowerCase().includes(this.filterValue) ||
        element.file?.toLowerCase().includes(this.filterValue) ||
        element.subCategoriaProducto
          ?.toLowerCase()
          .includes(this.filterValue) ||
        element.categoriaProducto?.toLowerCase().includes(this.filterValue)
    );

    if (this.selected) {
      result = result.filter(
        (el: any) => el.subCategoriaProducto === this.selected
      );
    }

    this.dataFilter = result;
  }

  applyFilterSelect() {
    console.log(this.dataInitial);
    let dataNew: any = this.dataInitial;

    if (this.selectFile) {
      dataNew = dataNew?.filter((item: any) => item.file === this.selectFile);
    }

    console.log(dataNew);
    this.dataFilter = dataNew;
  }

  changeValue(type: string | null) {
    console.log(this.filterValue);

    console.log(type);

    if (type) {
      this.selected = type;

      const result = this.dataInitial.filter(
        (item: any) => item.subCategoriaProducto === this.selected
      );

      if (this.filterValue) {
        let result2: any = result?.filter(
          (element: any) =>
            element.servicio?.toLowerCase().includes(this.filterValue) ||
            element.file?.toLowerCase().includes(this.filterValue) ||
            element.subCategoriaProducto
              ?.toLowerCase()
              .includes(this.filterValue) ||
            element.categoriaProducto?.toLowerCase().includes(this.filterValue)
        );

        this.dataFilter = result2;
      } else {
        this.dataFilter = result;
      }
    } else {
      this.selected = null;

      if (this.filterValue && this.filterValue !== '') {
        let result2: any = this.dataInitial?.filter(
          (element: any) =>
            element.servicio?.toLowerCase().includes(this.filterValue) ||
            element.file?.toLowerCase().includes(this.filterValue) ||
            element.subCategoriaProducto
              ?.toLowerCase()
              .includes(this.filterValue) ||
            element.categoriaProducto?.toLowerCase().includes(this.filterValue)
        );

        this.dataFilter = result2;
      } else {
        this.dataFilter = this.dataInitial;
      }
    }
  }
}
